import dayjs from 'dayjs'
import { ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useSetTrackerStatus, useUpdateTracker, extractErrorMessage } from '@/queries/ticketMutations'
import { useTrackerCategories } from '@/queries/tracker'
import { isAdminViewer } from '@/lib/scope'
import {
  trackerStatusLabels,
  trackerStatusVariants,
  trackerStatusOrder,
  devStageLabels,
  devStageVariants,
} from '@/lib/status'
import type { TrackerTicket, TrackerStatus } from '@/types/tracker'
import type { ViewerRelation } from '@/types/ticket'

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[80px_1fr] items-baseline gap-2 text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-foreground break-words min-w-0">{children}</span>
    </div>
  )
}

export function TrackerPanel({
  ticket,
  viewerRelation,
}: {
  ticket: TrackerTicket
  viewerRelation: ViewerRelation
}) {
  const isStaff = isAdminViewer(viewerRelation)
  const setStatus = useSetTrackerStatus(ticket._id)
  const updateTracker = useUpdateTracker(ticket._id)
  // Disabled for a client via the undefined companyId arg — nobody but staff
  // edits category, so nobody but staff needs the list fetched.
  const categoriesQuery = useTrackerCategories(isStaff ? ticket.companyId : undefined)
  const categories = categoriesQuery.data ?? []
  const { tracker } = ticket

  const changeStatus = async (status: TrackerStatus) => {
    try {
      await setStatus.mutateAsync({ status })
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update status'))
    }
  }

  // Whitelist-shaped like the server's PATCH /:id/tracker — one field per
  // call, so devStage/status (their own dedicated paths) can never be smuggled
  // in alongside a category/impact/expectedDate edit.
  const patchTracker = async (input: Parameters<typeof updateTracker.mutateAsync>[0]) => {
    try {
      await updateTracker.mutateAsync(input)
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update tracker'))
    }
  }

  // Only staff can pick freely; a client's sole write path is the
  // proposed_closed confirm/reject pair rendered below (design doc "Access
  // and scoping") — every other status is display-only for them.
  const clientCanAct = !isStaff && tracker.status === 'proposed_closed'

  return (
    <div className="divide-y divide-border">
      <section className="px-5 py-4 space-y-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Tracker</h3>

        <div className="space-y-2.5">
          <Row label="Status">
            {isStaff ? (
              <select
                aria-label="Tracker status"
                value={tracker.status}
                disabled={setStatus.isPending}
                onChange={e => changeStatus(e.target.value as TrackerStatus)}
                className="border rounded px-2 py-1 text-sm"
              >
                {trackerStatusOrder.map(s => (
                  <option key={s} value={s}>
                    {trackerStatusLabels[s]}
                  </option>
                ))}
              </select>
            ) : (
              <Badge variant={trackerStatusVariants[tracker.status]}>{trackerStatusLabels[tracker.status]}</Badge>
            )}
          </Row>

          {clientCanAct && (
            <div className="flex gap-2 pt-0.5">
              <Button size="xs" disabled={setStatus.isPending} onClick={() => changeStatus('closed')}>
                Confirm closed
              </Button>
              <Button size="xs" variant="outline" disabled={setStatus.isPending} onClick={() => changeStatus('accepted')}>
                Reopen
              </Button>
            </div>
          )}

          <Row label="Progress">
            <Badge variant={devStageVariants[tracker.devStage]}>{devStageLabels[tracker.devStage]}</Badge>
          </Row>

          {ticket.linearIssueUrl && (
            <Row label="Linear">
              <a
                href={ticket.linearIssueUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-foreground hover:underline"
              >
                View issue <ExternalLink className="h-3 w-3" />
              </a>
            </Row>
          )}

          {(isStaff || tracker.category) && (
            <Row label="Category">
              {isStaff ? (
                <Select
                  value={tracker.category ?? ''}
                  onValueChange={value => patchTracker({ category: value })}
                  disabled={updateTracker.isPending || categoriesQuery.isLoading}
                >
                  <SelectTrigger aria-label="Tracker category" className="h-8 w-full text-sm">
                    <SelectValue placeholder={categoriesQuery.isLoading ? 'Loading categories…' : 'Choose a category'} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                tracker.category
              )}
            </Row>
          )}

          {(isStaff || tracker.impact) && (
            <Row label="Impact">
              {isStaff ? (
                // Uncontrolled + remounted on the server value's own change
                // (key): buffers keystrokes locally and commits once on blur,
                // rather than firing a PATCH per character the way the
                // discrete status/category selects fire on every choice.
                <Textarea
                  key={tracker.impact ?? ''}
                  defaultValue={tracker.impact ?? ''}
                  aria-label="Tracker impact"
                  rows={2}
                  className="text-sm"
                  disabled={updateTracker.isPending}
                  onBlur={e => {
                    const value = e.target.value
                    if (value !== (tracker.impact ?? '')) patchTracker({ impact: value })
                  }}
                />
              ) : (
                tracker.impact
              )}
            </Row>
          )}

          {(isStaff || tracker.expectedDate) && (
            <Row label="Expected">
              {isStaff ? (
                <Input
                  type="date"
                  aria-label="Tracker expected date"
                  className="h-8 w-auto text-sm"
                  // Plain slicing, not dayjs formatting/parsing, on both sides
                  // of this round-trip: dayjs parses a bare 'YYYY-MM-DD' as
                  // LOCAL midnight, so in any positive-UTC-offset timezone
                  // (all of Europe in summer, for one) dayjs(value).toISOString()
                  // rolls the date back a day. The stored value is already an
                  // ISO string, so its first 10 characters ARE the calendar
                  // date — no timezone math needed to read it back into the
                  // input, and `new Date(value)` (a bare date, no time) is UTC
                  // midnight per spec, unlike dayjs's parse of the same string.
                  value={tracker.expectedDate ? tracker.expectedDate.slice(0, 10) : ''}
                  disabled={updateTracker.isPending}
                  onChange={e => {
                    const value = e.target.value
                    patchTracker({ expectedDate: value ? new Date(value).toISOString() : null })
                  }}
                />
              ) : (
                // Calendar-date semantics: format the UTC date string directly. dayjs()
                // converts to local time first, which shows the previous day anywhere
                // west of UTC.
                dayjs(tracker.expectedDate!.slice(0, 10)).format('D MMM YYYY')
              )}
            </Row>
          )}
          <Row label="Promoted">{dayjs(tracker.promotedAt).format('D MMM YYYY')}</Row>
        </div>
      </section>
    </div>
  )
}
